import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config } from 'services/config';
import {
  Report,
  Addendum,
  ReportUpdatePayload,
  ReportListParams,
  ReportListResponse,
  PersonnelOption,
} from '@/models/report.ts';
import { ReportEvent } from '@/models/reportEvent.ts';
import { Template, Worksheet } from '@/models/template.ts';
import { Pagination } from '@/models';
import { prepareHeaders, buildQueryString } from '@/state/slices/index.ts';
import {
  invalidateReportPdf,
  invalidateStudyCaches,
} from '@/state/slices/studies';

interface AddendumResult extends Addendum {
  report?: Report;
}

export const ReportAPI = createApi({
  reducerPath: 'ReportAPI',
  baseQuery: fetchBaseQuery({
    baseUrl: config.BASE_URL,
    prepareHeaders,
  }),
  tagTypes: ['Templates', 'Worksheets', 'Reports', 'ReportEvents'],
  endpoints: (builder) => ({
    // Short-lived signed link for a guideline PDF referenced by a measurement's
    // reference ranges. Never cached: each click gets a fresh link.
    getGuidelineUrl: builder.query<{ url: string }, string>({
      query: (file) => ({
        url: `/codings/guidelines/${encodeURIComponent(file)}/`,
      }),
      keepUnusedDataFor: 0,
    }),
    getTemplates: builder.query<Pagination<Template>, void>({
      query: () => ({
        url: `/templates/`,
        method: 'GET',
      }),
      providesTags: () => [{ type: 'Templates', id: 'LIST' }],
    }),
    getTemplate: builder.query<Template, string>({
      query: (uuid: string) => ({
        url: `/templates/${uuid}/`,
        method: 'GET',
      }),
      providesTags: (_result, _error, uuid) => [
        { type: 'Templates', id: uuid },
      ],
    }),
    getWorksheets: builder.query<Pagination<Worksheet>, void>({
      query: () => ({
        url: `/worksheets/`,
        method: 'GET',
      }),
      providesTags: () => [{ type: 'Worksheets', id: 'LIST' }],
    }),
    getWorksheet: builder.query<Worksheet, string>({
      query: (uuid: string) => ({
        url: `/worksheets/${uuid}/`,
        method: 'GET',
      }),
      providesTags: (_result, _error, uuid) => [
        { type: 'Worksheets', id: uuid },
      ],
    }),
    getReport: builder.query<Report, string>({
      query: (reportUuid) => ({
        url: `/reports/${reportUuid}/`,
        method: 'GET',
      }),
      transformResponse: (response: Report) => ({
        ...response,
        isFinalized: response.status === 'FINAL',
      }),
      providesTags: (_result, _error, reportUuid) => [
        { type: 'Reports', id: reportUuid },
      ],
    }),

    getStudyReports: builder.query<Pagination<Report>, string>({
      query: (studyUuid) => ({
        url: `/reports/`,
        method: 'GET',
        params: { study_uuid: studyUuid },
      }),
      transformResponse: (response: Pagination<Report>) => ({
        ...response,
        results: response.results.map((r) => ({
          ...r,
          isFinalized: r.status === 'FINAL',
        })),
      }),
      providesTags: (result, _error, studyUuid) =>
        result
          ? [
              ...result.results.map((r) => ({
                type: 'Reports' as const,
                id: r.uuid,
              })),
              { type: 'Reports' as const, id: `STUDY_${studyUuid}` },
            ]
          : [{ type: 'Reports' as const, id: `STUDY_${studyUuid}` }],
    }),

    getReportEvents: builder.query<Pagination<ReportEvent>, string>({
      query: (studyUuid) => ({
        url: `/report-events/?study_uuid=${studyUuid}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, studyUuid) => [
        { type: 'ReportEvents', id: studyUuid },
      ],
    }),

    finalizeReport: builder.mutation<Report, string>({
      query: (uuid: string) => ({
        url: `reports/${uuid}/finalize/`,
        method: 'POST',
        body: {},
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (_result, _error, uuid) => [
        { type: 'Reports', id: uuid },
        { type: 'Reports', id: 'LIST' },
      ],
      async onQueryStarted(_uuid, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.studyUuid) {
            dispatch(
              ReportAPI.util.invalidateTags([
                { type: 'Reports', id: `STUDY_${data.studyUuid}` },
                { type: 'ReportEvents', id: data.studyUuid },
              ])
            );
            dispatch(invalidateStudyCaches(data.studyUuid));
          }
        } catch {
          /* mutation failed, no-op */
        }
      },
    }),

    updateReport: builder.mutation<Report, ReportUpdatePayload>({
      query: (payload: ReportUpdatePayload) => ({
        url: `reports/${payload.uuid}/`,
        method: 'PATCH',
        body: payload,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (_result, _error, payload) => [
        { type: 'Reports', id: payload.uuid },
        { type: 'Reports', id: 'LIST' },
      ],
      async onQueryStarted(_payload, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.studyUuid) {
            dispatch(
              ReportAPI.util.invalidateTags([
                { type: 'Reports', id: `STUDY_${data.studyUuid}` },
                { type: 'ReportEvents', id: data.studyUuid },
              ])
            );
            dispatch(invalidateReportPdf(data.studyUuid));
          }
        } catch {
          /* mutation failed, no-op */
        }
      },
    }),

    listAllReports: builder.query<ReportListResponse, ReportListParams>({
      query: (params) => {
        const queryString = buildQueryString(params);
        return { url: `/reports/${queryString}`, method: 'GET' };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.results.map((r) => ({
                type: 'Reports' as const,
                id: r.uuid,
              })),
              { type: 'Reports' as const, id: 'LIST' },
            ]
          : [{ type: 'Reports' as const, id: 'LIST' }],
    }),

    getReferringPhysicians: builder.query<Pagination<PersonnelOption>, void>({
      query: () => ({ url: `/referring-physicians/`, method: 'GET' }),
    }),

    getSonographers: builder.query<Pagination<PersonnelOption>, void>({
      query: () => ({ url: `/sonographers/`, method: 'GET' }),
    }),

    createaddendum: builder.mutation<
      AddendumResult,
      { description: string; reason: string; studyUuid: string }
    >({
      query: (payload) => ({
        url: `report-addendums/`,
        method: 'POST',
        body: payload,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (result, _error, arg) => {
        const tags: { type: 'Reports'; id: string }[] = [
          { type: 'Reports', id: 'LIST' },
          { type: 'Reports', id: `STUDY_${arg.studyUuid}` },
        ];
        if (result?.report?.uuid) {
          tags.push({ type: 'Reports', id: result.report.uuid });
        }
        return tags;
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            ReportAPI.util.invalidateTags([
              { type: 'ReportEvents', id: arg.studyUuid },
            ])
          );
          dispatch(invalidateStudyCaches(arg.studyUuid));
        } catch {
          /* mutation failed, no-op */
        }
      },
    }),
    updateWorksheet: builder.mutation<Worksheet, Worksheet>({
      query: (worksheet) => ({
        url: `/worksheets/${worksheet.uuid}/`,
        method: 'PUT',
        body: worksheet,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: (_result, _error, worksheet) => [
        { type: 'Worksheets', id: worksheet.uuid },
        { type: 'Worksheets', id: 'LIST' },
      ],
    }),
    deleteWorksheet: builder.mutation<void, string>({
      query: (uuid) => ({
        url: `/worksheets/${uuid}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, uuid) => [
        { type: 'Worksheets', id: uuid },
        { type: 'Worksheets', id: 'LIST' },
      ],
    }),
  }),
});
export const {
  useLazyGetGuidelineUrlQuery,
  useGetTemplatesQuery,
  useGetTemplateQuery,
  useGetWorksheetsQuery,
  useGetWorksheetQuery,
  useGetReportQuery,
  useGetStudyReportsQuery,
  useGetReportEventsQuery,
  useListAllReportsQuery,
  useGetReferringPhysiciansQuery,
  useGetSonographersQuery,
  useFinalizeReportMutation,
  useUpdateReportMutation,
  useCreateaddendumMutation,
  useUpdateWorksheetMutation,
  useDeleteWorksheetMutation,
} = ReportAPI;
